import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  TooltipProps,
  ReferenceLine,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { useTheme } from "@/context/ThemeContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import { ExistingCustomerReplacementEfficiencyDataItem } from "@/modules/stochastic/features/DashboardPage/api/fetchStochasticDashboardData/types";

type Props = {
  initialData?: ExistingCustomerReplacementEfficiencyDataItem[];
};

const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  const { theme } = useTheme();

  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    const styles = {
      backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
      borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
      color: theme === "dark" ? "#F3F4F6" : "#1F2937",
    };

    return (
      <div
        className="p-3 border rounded text-sm shadow-lg space-y-2 max-w-xs"
        style={styles}
      >
        <div className="font-semibold">Year: {label}</div>
        <div className="space-y-1">
          <div>
            <span className="font-medium">Efficiency Score:</span>{" "}
            <span className="font-bold text-indigo-600">{data.efficiency}</span>
          </div>
          <div>
            <span className="font-medium">Grade:</span>{" "}
            <span
              className={`font-bold ${
                data.quintileGrade === "A"
                  ? "text-green-600"
                  : data.quintileGrade === "B"
                    ? "text-blue-600"
                    : data.quintileGrade === "C"
                      ? "text-yellow-600"
                      : data.quintileGrade === "D"
                        ? "text-orange-600"
                        : data.quintileGrade === "F"
                          ? "text-red-600"
                          : "text-gray-600"
              }`}
            >
              {data.quintileGrade}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <div>Non-Replacement Calls: {data.nonReplacementCalls}</div>
          <div>Replacement Sales: {data.replacementSales}</div>
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
            <div className="italic">
              Calculation: {data.nonReplacementCalls} ÷ {data.replacementSales}{" "}
              = {data.efficiency}
            </div>
            <div className="italic">
              Lower scores indicate better performance
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const ExistingCustomerReplacementEfficiencyChart: React.FC<Props> = ({
  initialData = [],
}) => {
  const { theme } = useTheme();
  const [selectedYear, setSelectedYear] = useState<number | null>(
    initialData.length > 0 ? initialData[initialData.length - 1].year : null,
  );

  const quintiles =
    initialData.length > 0
      ? initialData[0].quintiles
      : {
          A: 0,
          B: 0,
          C: 0,
          D: 0,
          F: 0,
        };

  const calculateYAxisDomain = () => {
    if (!initialData || initialData.length === 0) {
      return [0, 10];
    }

    const allValues = initialData.flatMap((item) => [
      item.efficiency,
      ...Object.values(item.quintiles),
    ]);

    const maxValue = Math.max(...allValues);
    const padding = maxValue * 0.1;

    return [0, Math.ceil(maxValue + padding)];
  };

  const [yMin, yMax] = calculateYAxisDomain();

  const currentYearData = selectedYear
    ? initialData.find((item) => item.year === selectedYear)
    : initialData[initialData.length - 1];

  const currentEfficiency = currentYearData?.efficiency ?? 0;
  const currentGrade = currentYearData?.quintileGrade ?? "N/A";

  return (
    <Card className="w-full bg-white dark:bg-secondary-dark">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-fontColor dark:text-light text-xl mb-2">
              Existing Customer Replacement Efficiency
            </CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Non-replacement calls ÷ replacement sales for House File customers
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-indigo-600">
              {currentEfficiency.toFixed(1)}
            </div>
            <div
              className={`text-lg font-semibold ${
                currentGrade === "A"
                  ? "text-green-600"
                  : currentGrade === "B"
                    ? "text-blue-600"
                    : currentGrade === "C"
                      ? "text-yellow-600"
                      : currentGrade === "D"
                        ? "text-orange-600"
                        : currentGrade === "F"
                          ? "text-red-600"
                          : "text-gray-600"
              }`}
            >
              Grade {currentGrade}
            </div>
            {selectedYear && (
              <div className="text-sm text-gray-500">Year {selectedYear}</div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="w-full h-80 md:h-96">
            <ResponsiveContainer height="100%" width="100%">
              <BarChart
                data={initialData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                onClick={(data) => {
                  if (data?.activeLabel) {
                    setSelectedYear(Number(data.activeLabel));
                  }
                }}
              >
                <CartesianGrid
                  stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="year"
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  tickFormatter={(value) => value.toFixed(1)}
                />
                <Tooltip content={<CustomTooltip />} />

                {/* Quintile Reference Lines with different colors */}
                <ReferenceLine
                  label={{ value: "A", position: "right" }}
                  stroke="#22c55e"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  y={quintiles.A}
                />
                <ReferenceLine
                  label={{ value: "B", position: "right" }}
                  stroke="#06b6d4"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  y={quintiles.B}
                />
                <ReferenceLine
                  label={{ value: "C", position: "right" }}
                  stroke="#fbbf24"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  y={quintiles.C}
                />
                <ReferenceLine
                  label={{ value: "D", position: "right" }}
                  stroke="#fb923c"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  y={quintiles.D}
                />
                <ReferenceLine
                  label={{ value: "F", position: "right" }}
                  stroke="#f87171"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  y={quintiles.F}
                />

                <Bar
                  cursor="pointer"
                  dataKey="efficiency"
                  fill="#6366f1"
                  onClick={(data) => setSelectedYear(data.year)}
                  opacity={0.8}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-green-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.A.toFixed(1)} Top 20%
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-cyan-500"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.B.toFixed(1)} Above Avg
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-amber-400"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.C.toFixed(1)} Average
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-orange-400"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                ≤{quintiles.D.toFixed(1)} Below Avg
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-red-400"></div>
              <span
                style={{
                  fontSize: 14,
                  color: theme === "dark" ? "#9CA3AF" : "#4B5563",
                }}
              >
                &gt;{quintiles.D.toFixed(1)} Bottom 20%
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExistingCustomerReplacementEfficiencyChart;
